import {
  ArrowStatusType,
  BrushFirmType,
  BrushType,
  HEXcolor,
  NameFirmType,
} from '@/shared/types'
import { Tooth } from '@/pages/BrushMapPage/tooth'
import { BrushMapTeeth } from '@/pages/BrushMapPage/brushMapTeeth'
import { BrushMapAddedBrushes } from '@/pages/BrushMapPage/brushMapAddedBrushes'
import { BrushMapFirm } from './brushMapFirm'
import {
  FirmIndexes,
  FirmWithAutoFillColors,
  LIST_COLORS_BY_NAME_FIRM as LISTS_COLORS_BY_NAME_FIRM,
} from '@/shared/constants/brushMapFirms'

export class BrushMapController {
  public brushMapAddedBrushes: BrushMapAddedBrushes
  public brushMapTeeth: BrushMapTeeth
  public brushMapFirm: BrushMapFirm
  private addedBrushes: Map<HEXcolor, number>
  private tooth: Tooth
  private selectedBrush: BrushType
  private selectedColor: HEXcolor
  private readonly DEFAULT_ARROW_STATUS: ArrowStatusType = 0
  private readonly DEFAULT_COLOR: HEXcolor = '#b3b3b3'

  constructor() {
    this.initiateBrushes()
  }

  public updateArrowState(tooth: Tooth): void {
    this.setValues(tooth)

    if (this.isDefaultSelectedColor()) {
      return
    }

    if (this.isChangeSameColors()) {
      this.handleChangeSameColors()
    } else {
      this.handleChangeDifferentColors()
    }
  }

  public updateTeethWithFirm(
    firm: FirmWithAutoFillColors,
    indexNewFirm: FirmIndexes
  ): void {
    for (const tooth of this.brushMapTeeth.teeth) {
      tooth.updateColorWithFirm(firm, indexNewFirm)
    }
  }

  public updateFirm(
    firm: FirmWithAutoFillColors,
    indexNewFirm: FirmIndexes,
    nameFirm: NameFirmType,
    firstGroup: string,
    secondGroup = 'none'
  ) {
    this.updateTeethWithFirm(firm, indexNewFirm)
    this.brushMapFirm.updateFirm({ nameFirm, firstGroup, secondGroup })
    this.addedBrushes.clear()
    this.setMapAddedBrushes()
    this.brushMapAddedBrushes.clearBrushes()
    this.setArrayAddedBrushes()
  }

  public deleteAddedBrushFromFirstGroup(brush: BrushType): void {
    this.brushMapAddedBrushes.deleteBrushOfFirstGroup(brush)
    this.addedBrushes.delete(brush.color)
    this.brushMapTeeth.deleteColorFromTeeth(brush.color)
  }

  public deleteAddedBrushFromSecondGroup(brush: BrushType): void {
    this.brushMapAddedBrushes.deleteBrushOfSecondGroup(brush)
    this.addedBrushes.delete(brush.color)
    this.brushMapTeeth.deleteColorFromTeeth(brush.color)
  }

  public setMapAddedBrushes(): void {
    for (const tooth of this.brushMapTeeth.teeth) {
      this.updateAddedBrushes(tooth)
    }
  }

  public setArrayAddedBrushes(): void {
    const currentNameFirm = this.brushMapFirm.firm.nameFirm
    const lists = LISTS_COLORS_BY_NAME_FIRM[currentNameFirm]
    for (const list of lists) {
      for (const color of this.addedBrushes.keys()) {
        const findedElement = list.find((brush) => brush.color === color)

        if (findedElement) {
          this.brushMapAddedBrushes.addBrush(findedElement)
        }
      }
    }
  }

  private updateAddedBrushes(tooth: Tooth): void {
    if (tooth.baseColor === this.DEFAULT_COLOR) {
      return
    }

    this.handleUpdateCountTeethWithSelectedColor(tooth.baseColor)
  }

  private isDefaultSelectedColor(): boolean {
    return this.selectedColor === this.DEFAULT_COLOR
  }

  private handleChangeSameColors(): void {
    this.tooth.updateArrowStatus()

    if (this.isDefaultArrowStatus()) {
      this.tooth.clearArrowColors()
      this.handleDeleteColorFromAddedBrushes(this.selectedColor)
      return
    }

    this.checkColors()
    this.tooth.updateArrowColor(this.selectedColor)
  }

  private handleChangeDifferentColors(): void {
    if (this.isMissSelectedColorInTeeth()) {
      this.handleMissSelectedColorInTeeth()
    } else {
      this.handleUpdateCountTeethWithSelectedColor()
    }

    this.handleDeleteColorFromAddedBrushes(this.tooth.baseColor)
    this.tooth.updateArrowColor(this.selectedColor)
  }

  public handleDeleteColorFromAddedBrushes(color: HEXcolor): void {
    if (!this.isCanDeleteColor(color)) {
      this.decreaseCountTeethWithColor(color)
      return
    }

    this.deleteColorFromBrushes(color)
  }

  private setValues(tooth: Tooth): void {
    this.tooth = tooth
    this.selectedBrush = this.brushMapAddedBrushes.selectedBrush
    this.selectedColor = this.selectedBrush.color
  }

  private checkColors(): void {
    if (this.isMissSelectedColorInTeeth()) {
      this.handleMissSelectedColorInTeeth()
      return
    }

    if (this.isDefaultArrowColor()) {
      this.handleUpdateCountTeethWithSelectedColor()
    }
  }

  private isChangeSameColors(): boolean {
    const isEqualColors = this.tooth.baseColor === this.selectedColor
    const isDefaultBaseColor = this.tooth.baseColor === this.DEFAULT_COLOR

    return isEqualColors || isDefaultBaseColor
  }

  private handleMissSelectedColorInTeeth(): void {
    this.brushMapAddedBrushes.addSelectedBrush()
    this.handleUpdateCountTeethWithSelectedColor()
  }

  private isDefaultArrowColor(): boolean {
    return this.tooth.baseColor === this.DEFAULT_COLOR
  }

  private isDefaultArrowStatus(): boolean {
    return this.tooth.arrowStatus === this.DEFAULT_ARROW_STATUS
  }

  public initiateBrushes(): void {
    this.addedBrushes = new Map()
  }

  private handleUpdateCountTeethWithSelectedColor(
    color = this.selectedColor
  ): void {
    if (this.haveColorInAddedBrushes(color)) {
      this.increaseCountTeethWithColor(color)
    } else {
      this.updateCountTeethWithColor(1, color)
    }
  }

  private haveColorInAddedBrushes(color: HEXcolor): boolean {
    return this.addedBrushes.has(color)
  }

  private increaseCountTeethWithColor(color: HEXcolor): void {
    const count = this.getCountTeethWithColor(color)
    this.updateCountTeethWithColor(count + 1, color)
  }

  private decreaseCountTeethWithColor(color: HEXcolor): void {
    const count = this.getCountTeethWithColor(color)
    this.updateCountTeethWithColor(count - 1, color)
  }

  private updateCountTeethWithColor(count: number, color: HEXcolor): void {
    this.addedBrushes.set(color, count)
  }

  private isMissSelectedColorInTeeth(): boolean {
    return !this.addedBrushes.has(this.selectedColor)
  }

  private getCountTeethWithColor(color: HEXcolor): number {
    return this.addedBrushes.get(color)
  }

  private deleteColorFromBrushes(color: HEXcolor): void {
    this.brushMapAddedBrushes.deleteBrush(color)
    this.addedBrushes.delete(color)
  }

  private isCanDeleteColor(color: HEXcolor): boolean {
    const MINIMAL_COUNT_FOR_DELETE = 1

    return this.getCountTeethWithColor(color) === MINIMAL_COUNT_FOR_DELETE
  }
}
